import { Check, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { GeneratedPrompt } from "@/hooks/use-generate-prompt";

interface GeneratedPromptPreviewProps {
  generated: GeneratedPrompt;
  onAccept: () => void;
  onRegenerate: () => void;
  onDismiss: () => void;
  isRegenerating: boolean;
}

export function GeneratedPromptPreview({
  generated,
  onAccept,
  onRegenerate,
  onDismiss,
  isRegenerating,
}: GeneratedPromptPreviewProps) {
  return (
    <Card className="border-primary/30 bg-primary/5 animate-in fade-in slide-in-from-top-2 duration-300">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            ✨ AI-Generated Prompt
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onDismiss} className="h-7 w-7">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Title</p>
          <p className="text-sm font-medium">{generated.title}</p>
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
          <p className="text-sm text-muted-foreground">{generated.description}</p>
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Prompt Content</p>
          <pre className="text-xs bg-background/80 rounded-md p-3 whitespace-pre-wrap max-h-48 overflow-y-auto border border-border/20">
            {generated.content}
          </pre>
        </div>

        {generated.keywords?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Keywords</p>
            <div className="flex flex-wrap gap-1">
              {generated.keywords.map((kw) => (
                <Badge key={kw} variant="secondary" className="text-xs">
                  {kw}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button onClick={onAccept} size="sm" className="gap-1.5 flex-1">
            <Check className="h-3.5 w-3.5" />
            Accept & Fill Form
          </Button>
          <Button
            onClick={onRegenerate}
            variant="outline"
            size="sm"
            disabled={isRegenerating}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRegenerating ? "animate-spin" : ""}`} />
            Regenerate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
